<template>
  <div class="awc-page">
    <div class="pg-title-row">
      <div>
        <h1 class="pg-title">Days Since Last LiveRun</h1>
        <p class="pg-title-sub">Review runner completion history and recency signals</p>
      </div>
    </div>

    <div class="awc-toolbar">
      <div>
        <h2 class="awc-toolbar-title">Completion recency</h2>
        <p class="awc-toolbar-subtitle">Sorted runner profiles with last LiveRun and completed run totals.</p>
      </div>
      <div class="awc-toolbar-actions">
        <runner-field-visibility-control
          modal-name="completedRunnersFieldVisibility"
          title="Completion Fields"
          subtitle="Choose the runner fields shown on this page"
          :field-options="fieldOptions"
          :selected-fields="selectedFieldIds"
          @apply="applyFieldSelection"
          @reset="resetFieldSelection"
        />
        <span class="awc-count-pill">{{ runners.length }} runners on this page</span>
      </div>
    </div>

    <div v-if="loading" class="awc-list">
      <div v-for="n in 6" :key="'awc-skeleton-' + n" class="awc-card awc-card--skeleton">
        <div class="awc-card-header">
          <div class="awc-skeleton-icon sk-shimmer"></div>
          <div class="awc-skeleton-heading">
            <div class="awc-skeleton-title sk-shimmer"></div>
            <div class="awc-skeleton-subtitle sk-shimmer"></div>
          </div>
        </div>
        <div class="awc-field-grid">
          <div v-for="field in 10" :key="'awc-field-' + n + '-' + field" class="awc-skeleton-field sk-shimmer"></div>
        </div>
      </div>
    </div>

    <div v-else-if="runners.length === 0" class="awc-empty">
      <span class="awc-empty-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="9"/>
          <path d="M12 7v5l3 2"/>
        </svg>
      </span>
      <h2 class="awc-empty-title">No completed-run records</h2>
      <p class="awc-empty-text">Runner completion data will appear here once it is available.</p>
    </div>

    <div v-else class="awc-list">
      <article v-for="(runner, index) in runners" :key="runner.id || index" class="awc-card">
        <div class="awc-card-header">
          <span class="awc-card-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="8" r="4"/>
              <path d="M4 21a8 8 0 0 1 16 0"/>
            </svg>
          </span>
          <div class="awc-runner-title">
            <h2 class="awc-runner-name">{{ displayValue(runner.runnerName, 'Unnamed runner') }}</h2>
            <div class="awc-runner-meta">
              <button class="awc-id-link" type="button" @click="getRunnersData(runner.id)">ID {{ displayValue(runner.id) }}</button>
              <template v-for="meta in runnerMetaFields(runner)">
                <span :key="meta.id + '-dot'" class="awc-meta-dot"></span>
                <span :key="meta.id">{{ meta.value }}</span>
              </template>
            </div>
          </div>
          <div v-if="isRunnerFieldVisible('days-since-last')" class="awc-recency">
            <span class="awc-recency-label">Days since last</span>
            <span class="awc-recency-value">{{ displayValue(runner.daysSinceLastClass, '0') }}</span>
          </div>
        </div>

        <div class="awc-field-grid">
          <div v-if="isRunnerFieldVisible('email')" class="awc-field awc-field--wide">
            <span class="awc-label">Email</span>
            <span class="awc-value awc-value--break">{{ displayValue(runner.email) }}</span>
          </div>
          <div v-if="isRunnerFieldVisible('account-create')" class="awc-field">
            <span class="awc-label">Account create</span>
            <span class="awc-value">{{ displayValue(runner.accountCreate) }}</span>
          </div>
          <div v-if="isRunnerFieldVisible('facebook')" class="awc-field">
            <span class="awc-label">Facebook</span>
            <span class="awc-value">{{ runner.facebook ? 'Yes' : 'No' }}</span>
          </div>
          <div v-if="isRunnerFieldVisible('trial-start')" class="awc-field">
            <span class="awc-label">Trial start</span>
            <span class="awc-value">{{ displayValue(runner.membershipStartDate) }}</span>
          </div>
          <div v-if="isRunnerFieldVisible('trial-end')" class="awc-field">
            <span class="awc-label">Trial end</span>
            <span class="awc-value">{{ displayValue(runner.membershipEndDate) }}</span>
          </div>
          <div v-if="isRunnerFieldVisible('completed-runs')" class="awc-field">
            <span class="awc-label">Completed runs</span>
            <span class="awc-value">{{ displayValue(runner.totalCompletedRuns, '0') }}</span>
          </div>
          <div v-if="isRunnerFieldVisible('last-liverun')" class="awc-field">
            <span class="awc-label">Last LiveRun</span>
            <span class="awc-value">{{ displayValue(runner.lastLiveRun) }}</span>
          </div>
          <div v-if="isRunnerFieldVisible('height')" class="awc-field">
            <span class="awc-label">Height</span>
            <span class="awc-value">{{ displayValue(runner.height) }}</span>
          </div>
          <div v-if="isRunnerFieldVisible('weight')" class="awc-field">
            <span class="awc-label">Weight</span>
            <span class="awc-value">{{ displayValue(runner.weight) }}</span>
          </div>
          <div v-if="isRunnerFieldVisible('promo-code')" class="awc-field">
            <span class="awc-label">Promo code</span>
            <span class="awc-value">{{ displayValue(runner.promoCode) }}</span>
          </div>
          <div v-if="isRunnerFieldVisible('website')" class="awc-field awc-field--wide">
            <span class="awc-label">Website</span>
            <span class="awc-value awc-value--break">{{ displayValue(runner.website) }}</span>
          </div>
          <div v-if="isRunnerFieldVisible('profile-text')" class="awc-field awc-field--full">
            <span class="awc-label">Profile text</span>
            <span class="awc-value awc-value--multiline">{{ runnerProfileText(runner) }}</span>
          </div>
        </div>
      </article>
    </div>

    <div v-if="!loading && totalPages > 0" class="awc-pagination-footer">
      <div class="awc-pagination-summary">Page {{ currentPage }} of {{ totalPages }}</div>
      <paginate
        :page-count="totalPages"
        :click-handler="handlePaginate"
        :prev-text="'Prev'"
        :next-text="'Next'"
        :container-class="'awc-pagination'"
        :page-class="'awc-pagination-item'"
        :page-link-class="'awc-pagination-link'"
        :prev-class="'awc-pagination-item'"
        :prev-link-class="'awc-pagination-link'"
        :next-class="'awc-pagination-item'"
        :next-link-class="'awc-pagination-link'"
        :active-class="'awc-pagination-active'"
        :disabled-class="'awc-pagination-disabled'">
      </paginate>
    </div>
  </div>
</template>

<script>
import Paginate from 'vuejs-paginate'
import { mapActions, mapGetters } from 'vuex'
import RunnerFieldVisibilityControl from './RunnerFieldVisibilityControl'
import {
  loadRunnerFieldPreference,
  resetRunnerFieldPreference,
  runnerFieldPreferencePages,
  saveRunnerFieldPreference
} from '@/utils/runnerFieldPreferences'

const fieldPreferenceConfig = runnerFieldPreferencePages.completedRuns

export default {
  name: 'AllRunners',
  data: () => ({
    currentPage: 1,
    loading: false,
    selectedFieldIds: loadRunnerFieldPreference(fieldPreferenceConfig.storageKey, fieldPreferenceConfig.fields)
  }),
  computed: {
    ...mapGetters({
      runners: 'runners/allRunners',
      totalPages: 'runners/totalPages'
    }),
    fieldOptions() {
      return fieldPreferenceConfig.fields
    }
  },
  methods: {
    async handlePaginate (page) {
      this.currentPage = page
      await this.loadRunners(page - 1)
    },
    async loadRunners(page) {
      this.loading = true
      try {
        await this.getAll(page)
      } finally {
        this.loading = false
      }
    },
    unicodeToChar(text) {
      if (text != null) {
        return String(text).replace(/\\u[\dA-F]{4}/gi, function (match) {
          return String.fromCharCode(parseInt(match.replace(/\\u/g, ''), 16))
        })
      }
      return text
    },
    displayValue(value, fallback) {
      const emptyFallback = fallback || '-'
      if (value === 0) return '0'
      if (value === false) return 'No'
      if (value === true) return 'Yes'
      if (value === null || value === undefined) return emptyFallback
      const stringValue = String(value).trim()
      return stringValue ? stringValue : emptyFallback
    },
    isRunnerFieldVisible(id) {
      return this.selectedFieldIds.indexOf(id) !== -1
    },
    runnerMetaFields(runner) {
      var fields = []
      if (this.isRunnerFieldVisible('location')) {
        fields.push({ id: 'location', value: this.displayValue(runner && runner.location) })
      }
      if (this.isRunnerFieldVisible('gender')) {
        fields.push({ id: 'gender', value: this.displayValue(runner && runner.gender) })
      }
      return fields
    },
    applyFieldSelection(selectedIds) {
      this.selectedFieldIds = saveRunnerFieldPreference(fieldPreferenceConfig.storageKey, this.fieldOptions, selectedIds)
    },
    resetFieldSelection() {
      this.selectedFieldIds = resetRunnerFieldPreference(fieldPreferenceConfig.storageKey, this.fieldOptions)
    },
    runnerProfileText(runner) {
      if (!runner || !runner.profileText) return '-'
      return this.displayValue(this.unicodeToChar(runner.profileText))
    },
    getRunnersData(id){
      let route = this.$router.resolve({
        name: 'runner-data',
        params: {
          runnerId: id,
          raceId: 2285
        }
      })
      window.open(route.href, '_blank')
    },
    ...mapActions({
      getAll: 'runners/getAllWithRunCompleted',
      clearAllRunners: 'runners/clearAllRunners'
    })
  },
  mounted () {
    this.loadRunners(0)
  },
  beforeDestroy () {
    this.clearAllRunners()
  },
  components: {
    Paginate,
    RunnerFieldVisibilityControl
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

.awc-page {
  font-family: 'Inter', sans-serif;
  color: #1a1a2e;
}

.awc-toolbar,
.awc-card,
.awc-empty,
.awc-pagination-footer {
  background: #fff;
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.awc-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  margin-bottom: 0.85rem;
}

.awc-toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.awc-toolbar-title {
  color: #1a1a2e;
  font-size: 14px;
  font-weight: 700;
  margin: 0;
}

.awc-toolbar-subtitle {
  color: #8a8a9a;
  font-size: 12px;
  margin: 4px 0 0;
}

.awc-count-pill {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  border-radius: 20px;
  background: #f0effe;
  color: #413b94;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
}

.awc-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.awc-card {
  padding: 1rem 1.25rem;
}

.awc-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 0.7rem;
  border-bottom: 1px solid #f0f0f5;
}

.awc-card-icon,
.awc-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
}

.awc-card-icon svg,
.awc-empty-icon svg {
  width: 16px;
  height: 16px;
}

.awc-runner-title {
  flex: 1;
  min-width: 0;
}

.awc-runner-name {
  color: #1a1a2e;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0;
}

.awc-runner-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  color: #8a8a9a;
  font-size: 12px;
  margin-top: 3px;
}

.awc-id-link {
  border: none;
  background: transparent;
  color: #4338ca;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  padding: 0;
}

.awc-meta-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #d8d8e6;
}

.awc-recency {
  min-width: 92px;
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  background: #fafafa;
  padding: 7px 10px;
  text-align: center;
}

.awc-recency-label,
.awc-label {
  color: #8a8a9a;
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
}

.awc-recency-value {
  display: block;
  color: #1a1a2e;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.1;
  margin-top: 3px;
}

.awc-field-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 7px;
  margin-top: 0.75rem;
}

.awc-field {
  min-width: 0;
  border: 1px solid #eaeaf0;
  border-radius: 8px;
  background: #fafafa;
  padding: 7px 9px;
}

.awc-field--wide {
  grid-column: span 2;
}

.awc-field--full {
  grid-column: 1 / -1;
}

.awc-label,
.awc-value {
  display: block;
}

.awc-label {
  margin-bottom: 3px;
}

.awc-value {
  color: #1a1a2e;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.3;
}

.awc-value--break {
  overflow-wrap: anywhere;
}

.awc-value--multiline {
  color: #4d4d61;
  font-weight: 500;
  max-height: 48px;
  overflow: hidden;
  white-space: pre-wrap;
}

.awc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  padding: 2rem;
  text-align: center;
}

.awc-empty-title {
  color: #1a1a2e;
  font-size: 16px;
  font-weight: 700;
  margin: 14px 0 5px;
}

.awc-empty-text {
  color: #8a8a9a;
  font-size: 13px;
  margin: 0;
}

.awc-pagination-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.85rem;
  padding: 0.85rem 1.1rem;
}

.awc-pagination-summary {
  color: #8a8a9a;
  font-size: 12px;
  font-weight: 600;
}

.awc-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.awc-pagination-item {
  display: inline-flex;
}

.awc-pagination-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  color: #5b4fcf;
  background: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 0 10px;
  text-decoration: none;
  cursor: pointer;
}

.awc-pagination-link:hover {
  background: #f0effe;
  border-color: #5b4fcf;
}

.awc-pagination-active .awc-pagination-link {
  background: #5b4fcf;
  border-color: #5b4fcf;
  color: #fff;
}

.awc-pagination-disabled .awc-pagination-link {
  color: #c8c8d8;
  cursor: default;
  background: #fafafa;
  border-color: #e8e8ed;
}

.awc-card--skeleton {
  pointer-events: none;
}

.awc-skeleton-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
}

.awc-skeleton-heading {
  flex: 1;
}

.awc-skeleton-title {
  width: 210px;
  height: 16px;
}

.awc-skeleton-subtitle {
  width: 320px;
  height: 11px;
  margin-top: 8px;
}

.awc-skeleton-field {
  height: 52px;
}

@media (max-width: 1100px) {
  .awc-field-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .awc-toolbar,
  .awc-card-header,
  .awc-pagination-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .awc-toolbar-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .awc-field-grid {
    grid-template-columns: 1fr;
  }

  .awc-field--wide {
    grid-column: auto;
  }
}
</style>



// WEBPACK FOOTER //
// AllWithRunCompleted.vue?40ce53c4